import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Household } from '@ousse/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Alert, ErrorAlert } from '../components/ui'

interface Preview {
  householdName: string
  role: 'owner' | 'member' | 'viewer'
  invitedBy: string
}

/** Lien d'invitation /join/CODE : aperçu, puis adhésion (ou connexion / inscription d'abord). */
export function JoinPage() {
  const { t } = useTranslation()
  const { code = '' } = useParams()
  const { me } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState<unknown>(null)
  const [joined, setJoined] = useState<Household | null>(null)
  const preview = useQuery({ queryKey: ['invitation', code], queryFn: () => api.get<Preview>(`/invitations/${encodeURIComponent(code)}`) })

  return (
    <>
      <h1>{t('household.joinTitle')}</h1>
      <ErrorAlert error={preview.error ?? error} />
      {preview.data && !joined && (
        <div className="stack">
          <p>{t('household.joinIntro', { invitedBy: preview.data.invitedBy, household: preview.data.householdName })}</p>
          <p className="muted">{t(`household.roles.${preview.data.role}`)}</p>
          {me ? (
            <button
              type="button"
              className="btn block"
              onClick={async () => {
                try {
                  setJoined(await api.post<Household>(`/invitations/${encodeURIComponent(code)}/accept`))
                  await qc.invalidateQueries({ queryKey: ['households'] })
                } catch (e) {
                  setError(e)
                }
              }}
            >
              {t('household.join')}
            </button>
          ) : (
            <>
              <p>{t('household.joinNeedAccount')}</p>
              <Link className="btn block" to={`/register?invitation=${encodeURIComponent(code)}`}>
                {t('auth.register')}
              </Link>
              <Link className="btn secondary block" to={`/login?next=${encodeURIComponent(`/join/${code}`)}`}>
                {t('auth.login')}
              </Link>
            </>
          )}
        </div>
      )}
      {joined && (
        <>
          <Alert kind="success">{t('household.joined', { household: joined.name })}</Alert>
          <button type="button" className="btn" onClick={() => navigate('/')}>
            {t('nav.home')}
          </button>
        </>
      )}
    </>
  )
}
